// Web addresses in a chat message made into links, as the phone's feedback
// chat does with NSDataDetector (FeedbackView.swift `linkified(_:linkColor:)`):
// written with or without `https://` — `www.` hosts, bare domains with a
// known ending, e-mail addresses (opened as `mailto:`).
//
// Only `http(s)`, `ftp` and `mailto` links are ever made, so a message can
// never carry a script into an `href`.

// Domain endings a bare address must have to count as one. Common
// endings only, and none that are also file extensions people type
// (`.py`, `.md`, `.sh`, `.rs`…): "notes.md" is a file, not a site.
const TLDS = [
  // generic
  "com", "org", "net", "edu", "gov", "mil", "int", "info", "biz", "name", "pro", "mobi", "asia",
  "travel", "jobs", "tel", "coop", "aero", "museum",
  "app", "dev", "io", "ai", "co", "me", "tv", "cc", "gg", "ly", "fm", "am", "to", "vc", "ws", "la",
  "xyz", "online", "site", "tech", "store", "shop", "blog", "news", "page", "live", "club", "cloud",
  "art", "design", "studio", "space", "website", "world", "life", "today", "digital", "agency",
  "academy", "school", "media", "network", "systems", "solutions", "software", "support", "email",
  "link", "click", "wiki", "fun", "top", "icu", "one", "ninja", "guru", "zone", "games", "video",
  // countries and regions
  "eu", "us", "uk", "ca", "au", "nz", "ie", "fr", "de", "es", "it", "pt", "nl", "be", "ch", "at",
  "se", "no", "dk", "fi", "is", "pl", "cz", "sk", "hu", "ro", "bg", "gr", "tr", "ru", "ua", "by",
  "lt", "lv", "ee", "si", "hr", "ba", "mk", "al", "lu", "li", "mc", "mt", "cy", "jp", "cn", "kr",
  "tw", "hk", "sg", "my", "id", "ph", "vn", "th", "in", "pk", "bd", "lk", "np", "ir", "iq", "il",
  "ae", "sa", "qa", "kw", "om", "bh", "jo", "lb", "eg", "ma", "dz", "tn", "ng", "ke", "za", "gh",
  "et", "tz", "ug", "sn", "ci", "cm", "br", "mx", "ar", "cl", "pe", "ve", "uy", "bo", "ec", "cr",
  "pa", "gt", "cu", "do", "pr", "su",
];

const LABEL = "[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]{0,61}[\\p{L}\\p{N}])?";
const AFTER_HOST = "(?![\\p{L}\\p{N}-])(?::\\d{1,5})?(?:[/?#][^\\s<>\"]*)?";

const ADDRESS = new RegExp(
  [
    // e-mail first, so its domain is not taken for a site
    `(?<email>[\\p{L}\\p{N}._%+-]+@(?:${LABEL}\\.)+\\p{L}{2,63}(?![\\p{L}\\p{N}-]))`,
    `(?<scheme>(?:https?|ftp):\\/\\/[^\\s<>"]+)`,
    `(?<mailto>mailto:[^\\s<>"]+@[^\\s<>"]+)`,
    `(?<www>www\\.(?:${LABEL}\\.)+\\p{L}{2,63}${AFTER_HOST})`,
    `(?<host>(?:${LABEL}\\.)+(?:${TLDS.join("|")})${AFTER_HOST})`,
  ].join("|"),
  "giu",
);

// A letter, digit or joining mark right before an address means it is the
// tail of a longer word ("my_site.com"), not an address of its own.
const GLUED_BEFORE = /[\p{L}\p{N}@._%+\-/\\]/u;

const PAIRS = { ")": "(", "]": "[", "}": "{" };

function count(text, ch) {
  let n = 0;
  for (const c of text) if (c === ch) n += 1;
  return n;
}

/** Sentence punctuation after an address is not part of it, nor is a
 *  closing bracket that opened before it: "(see wellnotes.ai)." */
function trimTrailing(text) {
  let end = text.length;
  while (end > 0) {
    const ch = text[end - 1];
    if (".,;:!?'*".includes(ch)) {
      end -= 1;
      continue;
    }
    const open = PAIRS[ch];
    if (open && count(text.slice(0, end), open) < count(text.slice(0, end), ch)) {
      end -= 1;
      continue;
    }
    break;
  }
  return text.slice(0, end);
}

function hrefFor(kind, text) {
  if (kind === "email") return `mailto:${text}`;
  if (kind === "scheme" || kind === "mailto") return text;
  return `http://${text}`;
}

/**
 * The message cut into `{ text, href }` parts, in order: `href` is the
 * link to open for an address, `null` for the text around it. Joined back,
 * the parts are exactly the message.
 */
export function linkParts(message) {
  const text = typeof message === "string" ? message : "";
  const parts = [];
  let last = 0;
  ADDRESS.lastIndex = 0;
  for (let match = ADDRESS.exec(text); match; match = ADDRESS.exec(text)) {
    const start = match.index;
    const kind = Object.keys(match.groups).find((key) => match.groups[key] !== undefined);
    if (start > 0 && GLUED_BEFORE.test(text[start - 1])) continue;
    const address = trimTrailing(match[0]);
    // Nothing left but the scheme: "https://." is no address.
    if (!address || /^(?:https?|ftp):\/\/$|^mailto:$/i.test(address)) continue;
    if (start > last) parts.push({ text: text.slice(last, start), href: null });
    parts.push({ text: address, href: hrefFor(kind, address) });
    last = start + address.length;
    ADDRESS.lastIndex = last;
  }
  if (last < text.length) parts.push({ text: text.slice(last), href: null });
  return parts;
}
